import { useContext, useMemo } from 'react'
import { useTranslations } from 'next-intl'

import flattenNavItems from '~/components/QuickSearch/utils/flattenNavItems'
import { AbilityContext } from '~/layouts/components/acl/Can'
import routeDirectory from '~/navigation/routes'
import useNavItems from '~/navigation/vertical'

/**
 * Single G-leader follow-up entry — keyboard key, displayable label, target
 * route. Consumed by both `useKeyboardShortcuts` (for the indicator's
 * `SequenceState`) and `Overlay` (for the help-overlay's navigation rows).
 */
export type ShortcutRoute = {
  /** Lowercase key the keydown handler matches against (`'d'`, `','`, etc.). */
  key: string
  /** Same key uppercased / display-ready for the UI (`'D'`, `','`, etc.). */
  displayKey: string
  /** Localized action label (`'Dashboard'`, `'Marketplace'`, etc.). */
  label: string
  /** Destination path passed to `router.push(...)`. */
  route: string
}

/**
 * Label keys live in `components.KeyboardShortcutsOverlay`. Encoded as a
 * literal-type alias so each spec's `labelKey` field is type-narrowed against
 * the locale-type augmentation (per AGENTS.md "Never weaken the type system
 * around translation keys").
 */
type ShortcutLabelKey =
  | 'navigation_dashboard'
  | 'navigation_vehicles'
  | 'navigation_sites'
  | 'navigation_chargers'
  | 'navigation_reports'
  | 'navigation_billing'
  | 'navigation_workflows'
  | 'navigation_settings'

type GShortcutSpec = {
  key: string
  displayKey: string
  route: string
  labelKey: ShortcutLabelKey
  /**
   * Set when the route is deliberately NOT in the sidebar — page-level auth
   * applies, and we always show the shortcut. Currently only `,` (settings
   * landing). When `false`/absent, the route MUST appear in the user's
   * flattened sidebar nav to be shown (existence + CASL gates apply).
   */
  offNav?: boolean
}

/**
 * Keyboard-specific opinion: which letter maps to which route. Stays here
 * (not in the nav config) because the letter assignments are a UX call —
 * e.g. `,` for Settings because `S` is taken by Sites, `B` for Billing
 * because the bare-`B` leader is preview-only.
 *
 * Order is preserved as the visible order in both the SequenceIndicator
 * and the help overlay: primary nav (`d/v/s/c/r`) first, secondary nav
 * (`b/w`) next, then `,` for settings.
 */
const G_SHORTCUT_SPECS: GShortcutSpec[] = [
  { key: 'd', displayKey: 'D', route: routeDirectory.dashboard.root, labelKey: 'navigation_dashboard' },
  { key: 'v', displayKey: 'V', route: routeDirectory.vehicles.root, labelKey: 'navigation_vehicles' },
  { key: 's', displayKey: 'S', route: routeDirectory.sites.root, labelKey: 'navigation_sites' },
  { key: 'c', displayKey: 'C', route: routeDirectory.chargers.root, labelKey: 'navigation_chargers' },
  { key: 'r', displayKey: 'R', route: routeDirectory.reports.root, labelKey: 'navigation_reports' },
  { key: 'b', displayKey: 'B', route: routeDirectory.settings.billingOverview, labelKey: 'navigation_billing' },
  { key: 'w', displayKey: 'W', route: routeDirectory.workflows.root, labelKey: 'navigation_workflows' },
  { key: ',', displayKey: ',', route: routeDirectory.settings.root, labelKey: 'navigation_settings', offNav: true }
]

/**
 * Returns the visible G-leader follow-up routes for the current user.
 *
 * Labels always come from the `components.KeyboardShortcutsOverlay` namespace
 * (`navigation_*` keys). The sidebar's own nav-config titles are *not* reused
 * for shortcut labels because they're written for sidebar context (e.g.
 * billing-overview is titled `t('overview')` under "Commerce & Billing" —
 * unambiguous as a sidebar row, but meaningless in isolation as `G B →
 * Overview` in the shortcuts surface). Per Copilot + Cursor Bugbot findings
 * on PR #2266.
 *
 * The sidebar nav (`useNavItems` → `flattenNavItems`) is consulted ONLY for
 * existence + ACL filtering:
 *
 *  1. **Existence** — if a NavLink with the matching `path` is missing from
 *     the user's flattened nav, the route was JS-gated off in `useNavItems`
 *     (feature flags, org settings, etc.) and we drop the shortcut.
 *
 *  2. **CASL** — when the NavLink carries `action` + `subject` (e.g. the
 *     `...adminOnly` spread on billing-overview), we run
 *     `ability.can(...)` and drop denied routes.
 *
 * Specs flagged `offNav: true` skip both checks and always render — used for
 * routes that exist but are deliberately not in the sidebar (`/settings/`
 * landing). Their access is gated at the page level.
 *
 * Replaces the hardcoded `routeMap` from PR #2134 (the regression source —
 * shortcuts were shown for pages the user couldn't access).
 */
export function useGShortcutRoutes(): ShortcutRoute[] {
  const navItems = useNavItems()
  const ability = useContext(AbilityContext)
  const t = useTranslations('components.KeyboardShortcutsOverlay')

  const flattened = useMemo(() => flattenNavItems(navItems), [navItems])

  return useMemo(() => {
    const result: ShortcutRoute[] = []
    for (const { key, displayKey, route, labelKey, offNav } of G_SHORTCUT_SPECS) {
      // Label always from the locale — context-free, suited to the shortcut
      // surface (avoids the "Overview" vs "Billing" mismatch — see PR #2266
      // Copilot finding).
      const label = t(labelKey)

      if (offNav) {
        // Route is deliberately not in the sidebar (page-level auth applies).
        result.push({ key, displayKey, label, route })
        continue
      }

      const navLink = flattened.find((link) => link.path === route)
      if (!navLink) {
        // JS-gated off in `useNavItems` — drop.
        continue
      }
      if (navLink.action && navLink.subject && ability && !ability.can(navLink.action, navLink.subject)) {
        // CASL denies — drop.
        continue
      }
      result.push({ key, displayKey, label, route })
    }
    return result
  }, [flattened, ability, t])
}
